import { F0Alert, F0Dialog, F0Text } from '@factorialco/f0-react'
import { Stack } from '@factorialco/f0-react/dist/experimental'

import i18n from 'modules/core/lib/i18n'

import { IssueItem } from 'modules/integrations/components/SyncButton/Modals/SyncReviewModal/IssueItem'
import { SyncIssue } from 'modules/integrations/hooks/SyncContext/issues'

import styles from './SyncReviewModal.module.css'

type Callout = {
  title: string
  description: string
}

type SyncReviewModalProps = {
  onClose: () => void
  onSubmit?: () => void
  issues: SyncIssue[]
  anyValidSyncable: boolean
  hasFilters?: boolean
  isExpenses?: boolean
  callouts?: Callout[]
  title: string
  description?: string
  submitLabel?: string
}

export function SyncReviewModal({
  onClose,
  onSubmit,
  issues,
  anyValidSyncable,
  hasFilters = false,
  isExpenses = false,
  callouts = [],
  title,
  description,
  submitLabel,
}: SyncReviewModalProps) {
  const hasIssues = issues.length > 0

  const getDialogActions = () => {
    // No valid syncables → nothing to sync, only allow close
    if (!anyValidSyncable) {
      return {
        primaryAction: undefined,
        secondaryAction: { label: i18n.t('common.close'), onClick: onClose },
      }
    }

    // No issues → allow sync/export
    if (!hasIssues) {
      return {
        primaryAction: onSubmit
          ? { label: submitLabel ?? i18n.t('common.confirm'), onClick: onSubmit }
          : undefined,
        secondaryAction: {
          label: onSubmit ? i18n.t('common.cancel') : i18n.t('common.close'),
          onClick: onClose,
        },
      }
    }

    // Has issues but some valid syncables → allow sync as secondary action (only if onSubmit provided)
    if (!onSubmit) {
      return {
        primaryAction: undefined,
        secondaryAction: { label: i18n.t('common.close'), onClick: onClose },
      }
    }

    return {
      primaryAction: undefined,
      secondaryAction: { label: submitLabel ?? i18n.t('common.confirm'), onClick: onSubmit },
    }
  }

  const { primaryAction, secondaryAction } = getDialogActions()

  const canSubmit = Boolean(onSubmit) && (!hasIssues || anyValidSyncable)
  const activeCallouts = canSubmit ? callouts : []

  return (
    <F0Dialog
      isOpen
      onClose={onClose}
      width="sm"
      title={title}
      description={hasIssues ? description : undefined}
      primaryAction={primaryAction}
      secondaryAction={secondaryAction}
    >
      <Stack gap="4">
        {hasIssues ? (
          <Stack gap="4" className={styles.issuesList}>
            {issues.map((issue, index) => (
              <IssueItem key={index} issue={issue} />
            ))}
          </Stack>
        ) : description ? (
          <F0Text content={description} />
        ) : null}
        {canSubmit && hasFilters && (
          <F0Alert
            variant="info"
            title={i18n.t('integrations.confirmation_modal.filters_callout.title')}
            description={i18n.t('integrations.confirmation_modal.filters_callout.description')}
          />
        )}
        {isExpenses && !hasIssues && (
          <F0Alert
            variant="info"
            title={i18n.t('integrations.confirmation_modal.expenses_callout.title')}
            description={i18n.t('integrations.confirmation_modal.expenses_callout.description')}
          />
        )}
        {activeCallouts.map((callout, index) => (
          <F0Alert
            key={index}
            variant="info"
            title={callout.title}
            description={callout.description}
          />
        ))}
      </Stack>
    </F0Dialog>
  )
}
